import { useRef, useState } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { Camera, Trash2 } from "lucide-react";
import "./UserAvatar.css";

const AVATAR_COLORS = ["#6f42c1", "#d63384", "#fd7e14", "#20c997", "#0d6efd", "#198754", "#dc3545", "#6610f2"];
const AVATAR_SIDE_PX = 256;

/** Фото користувача (avatarUrl), а якщо його немає — кружок з ініціалами. */
export default function UserAvatar({ user, size = 40 }) {
    const [broken, setBroken] = useState(false);
    const p = user?.profile || {};
    const style = { width: size, height: size, opacity: user?.blocked ? 0.45 : 1 };

    if (user?.avatarUrl && !broken) {
        return (
            <img
                src={user.avatarUrl}
                alt=""
                className="rounded-circle flex-shrink-0"
                style={{ ...style, objectFit: "cover" }}
                onError={() => setBroken(true)}
            />
        );
    }

    const first = p.firstName || user?.keycrm?.firstName || user?.username || "?";
    const last = p.lastName || user?.keycrm?.lastName || "";
    const initials = (first.trim()[0] || "?") + (last.trim()[0] || "");
    const seed = String(p.keycrmManagerId ?? user?._id ?? "");
    let h = 0;
    for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return (
        <div
            className="d-flex align-items-center justify-content-center rounded-circle text-white fw-semibold flex-shrink-0"
            style={{ ...style, fontSize: Math.round(size * 0.38), background: AVATAR_COLORS[h % AVATAR_COLORS.length] }}
        >
            {initials.toUpperCase()}
        </div>
    );
}

/** Обрізає по центру в квадрат і зменшує до 256px → JPEG data URL (~20–40 КБ). */
export function fileToAvatarDataUrl(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const side = Math.min(img.naturalWidth, img.naturalHeight);
            const canvas = document.createElement("canvas");
            canvas.width = AVATAR_SIDE_PX;
            canvas.height = AVATAR_SIDE_PX;
            canvas
                .getContext("2d")
                .drawImage(
                    img,
                    (img.naturalWidth - side) / 2,
                    (img.naturalHeight - side) / 2,
                    side,
                    side,
                    0,
                    0,
                    AVATAR_SIDE_PX,
                    AVATAR_SIDE_PX
                );
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Не вдалося прочитати зображення"));
        };
        img.src = url;
    });
}

/** Стискає файл і зберігає як аватар користувача; повертає оновленого користувача. */
export async function uploadUserAvatar(userId, file) {
    const image = await fileToAvatarDataUrl(file);
    const res = await axios.put(`/api/users/${userId}/avatar`, { image }, { withCredentials: true });
    return res.data;
}

export async function removeUserAvatar(userId) {
    const res = await axios.delete(`/api/users/${userId}/avatar`, { withCredentials: true });
    return res.data;
}

/**
 * Кружок аватара з керуванням по наведенню: по центру іконка камери (додати / змінити фото),
 * у кутку — кошик (видалити), якщо фото є. На тач-пристроях іконки видно завжди.
 */
export function AvatarHoverControl({ user, previewSrc, size = 72, busy = false, onPick, onRemove }) {
    const inputRef = useRef(null);
    const hasPhoto = !!(previewSrc || user?.avatarUrl);

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) onPick?.(file);
    };

    return (
        <div className={`avatar-control${busy ? " avatar-control--busy" : ""}`} style={{ width: size, height: size }}>
            {previewSrc ? (
                <img src={previewSrc} alt="" className="rounded-circle" style={{ width: size, height: size, objectFit: "cover" }} />
            ) : (
                <UserAvatar key={user?.avatarUrl || "none"} user={user} size={size} />
            )}
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
            <button
                type="button"
                className="avatar-control__pick"
                disabled={busy}
                title={hasPhoto ? "Змінити фото" : "Додати фото"}
                aria-label={hasPhoto ? "Змінити фото" : "Додати фото"}
                onClick={() => inputRef.current?.click()}
            >
                {busy ? <Spinner size="sm" animation="border" /> : <Camera size={Math.round(size * 0.3)} aria-hidden />}
            </button>
            {hasPhoto && onRemove && !busy ? (
                <button type="button" className="avatar-control__remove" title="Видалити фото" aria-label="Видалити фото" onClick={onRemove}>
                    <Trash2 size={13} aria-hidden />
                </button>
            ) : null}
        </div>
    );
}

/** Аватар користувача з завантаженням / видаленням одразу на сервер. onChange отримує оновленого користувача. */
export function AvatarEditor({ user, onChange, size = 72 }) {
    const [busy, setBusy] = useState(false);

    const handlePick = async (file) => {
        setBusy(true);
        try {
            onChange?.(await uploadUserAvatar(user._id, file));
        } catch (err) {
            alert(err.response?.data?.error || err.message || "Не вдалося завантажити фото");
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async () => {
        if (!window.confirm("Видалити фото?")) return;
        setBusy(true);
        try {
            onChange?.(await removeUserAvatar(user._id));
        } catch (err) {
            alert(err.response?.data?.error || "Не вдалося видалити фото");
        } finally {
            setBusy(false);
        }
    };

    return <AvatarHoverControl user={user} size={size} busy={busy} onPick={handlePick} onRemove={handleRemove} />;
}
